import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, Label, Switch } from '@headlessui/react'
import { t } from 'i18next'
import { useLazyQuery, useMutation } from '@apollo/client'
import loadingImage from 'assets/images/boat-loading.gif'
import { GET_SYS_CONF_SERVICE_PAYMENT } from 'graphql/common/queries'
import { UPDATE_SERVICE_PAYMENT } from 'graphql/common/mutations'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'

export function ConfigServicePayment() {
    const { t } = useTranslation()
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [configPaymentList, setConfigPaymentList] = useState(null)
    const [changedIndexes, setChangedIndexes] = useState([])

    const [getConfigPaymentList, { loading: servicePaymentListLoading, error: servicePaymentListError }] = useLazyQuery(
        GET_SYS_CONF_SERVICE_PAYMENT,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getServicePayments.success && data.getServicePayments.code === '200') {
                    setConfigPaymentList(data.getServicePayments.service_payments)
                }
            },
        }
    )
    const getSystemPaymentConfig = () => {
        getConfigPaymentList({
            variables: {
                serviceCode: null,
            },
        })
    }
    useEffect(() => {
        getSystemPaymentConfig()
    }, [])

    const handleDialogClose = () => {
        setShowErrDialog(false)
        setShowSucDialog(false)
    }
    const handleSwitchChange = (index, method, checked) => {
        const updatedPaymentList = [...configPaymentList]
        const updatedPaymentOptions = { ...updatedPaymentList[index].payment_options }
        updatedPaymentOptions[method] = checked
        updatedPaymentList[index] = { ...updatedPaymentList[index], payment_options: updatedPaymentOptions }
        setConfigPaymentList(updatedPaymentList)
        setChangedIndexes(prev => [...new Set([...prev, index])])
    }
    // Mutation Update config
    const [updatePayment, { loading: updateLoading }] = useMutation(UPDATE_SERVICE_PAYMENT, {
        onCompleted: data => {
            if (data?.updateServicePayment.code === '200' && data?.updateServicePayment.success === true) {
                setShowSucDialog(true)
                setDialogMessage(data?.updateServicePayment.message)
                setTimeout(() => {
                    setShowSucDialog(false)
                    getSystemPaymentConfig()
                }, 1000)
            } else {
                setDialogMessage(data?.updateBoat.message)
                setShowErrDialog(true)
            }
        },
        onError: error => {
            setDialogMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const handleSave = index => {
        const paymentOption = {
            CASH: null,
            WALLET: null,
            DEBIT_CARD: null,
            CREDIT_CARD: null,
            APPLE_PAY: null,
            GOOGLE_PAY: null,
        }

        const payment = configPaymentList[index]
        Object.keys(payment.payment_options).forEach(option => {
            if (option !== '__typename') {
                paymentOption[option] = payment.payment_options[option]
            }
        })
        console.log({
            serviceCode: payment.service_code,
            paymentOption,
        })
        setChangedIndexes(prev => prev.filter(i => i !== index))
        updatePayment({
            variables: {
                serviceCode: payment.service_code,
                paymentOption,
            },
        })
    }
    return (
        <>
            {servicePaymentListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {servicePaymentListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch records!')}</div>
                </div>
            )}
            {!servicePaymentListLoading && !servicePaymentListError && configPaymentList?.length === 0 && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No data found!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {!servicePaymentListLoading && !servicePaymentListError && configPaymentList?.length > 0 && (
                <div className="py-1 mt-2">
                    {configPaymentList.map((paymentsInfo, index) => (
                        <div
                            key={index}
                            className="grid grid-cols-1 gap-x-8 gap-y-4 border-b border-gray-900/10 pb-5 md:grid-cols-3 mt-5"
                        >
                            <div>
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {paymentsInfo.service.description.en}
                                </h2>
                                <p className="mt-1 text-sm leading-6 text-gray-600">
                                    {t('Choose your preferred payment method for this trips.')}
                                </p>
                            </div>
                            <div className="grid max-w-xl grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-6 md:col-span-2">
                                {['CASH', 'WALLET', 'DEBIT_CARD', 'CREDIT_CARD', 'APPLE_PAY', 'GOOGLE_PAY'].map((method, idx) => (
                                    <div key={idx} className="sm:col-span-full">
                                        <div className="overflow-hidden rounded-lg bg-white ring-1 ring-inset ring-gray-300">
                                            <div className="px-4 py-3 sm:p-3">
                                                <Field className="flex items-center justify-between">
                                                    <span className="flex flex-grow flex-col">
                                                        <Label
                                                            as="span"
                                                            passive
                                                            className="text-sm font-medium leading-6 text-gray-900"
                                                        >
                                                            {t(method.replace('_', ' '))}
                                                        </Label>
                                                    </span>
                                                    <Switch
                                                        onChange={checked => handleSwitchChange(index, method, checked)}
                                                        checked={paymentsInfo.payment_options[method]}
                                                        className={`${
                                                            paymentsInfo.payment_options[method]
                                                                ? 'bg-primary'
                                                                : 'bg-gray-200'
                                                        } group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2`}
                                                    >
                                                        <span
                                                            aria-hidden="true"
                                                            className={`${
                                                                paymentsInfo.payment_options[method]
                                                                    ? 'translate-x-5'
                                                                    : 'translate-x-0'
                                                            } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                                                        />
                                                    </Switch>
                                                </Field>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div className="sm:col-span-full">
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            data-autofocus
                                            className={`inline-flex items-center justify-center rounded-md px-8 py-2 text-sm font-semibold text-white shadow-sm ${
                                                changedIndexes.includes(index)
                                                    ? 'bg-primary hover:bg-primary/[0.9]'
                                                    : 'bg-gray-400 cursor-not-allowed'
                                            }`}
                                            onClick={() => handleSave(index)}
                                            disabled={!changedIndexes.includes(index)}
                                        >
                                            {t('Save')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={dialogMessage} onClose={handleDialogClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={dialogMessage} onClose={handleDialogClose} />
            )}
            {updateLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
        </>
    )
}
